import { FC, ReactNode, SyntheticEvent, useState } from 'react';

import { Accordion, AccordionDetails, AccordionSummary, Box, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useInView } from 'react-intersection-observer';
import { Element } from 'react-scroll';
import config from 'src/config';

import FontAwesomeIcon from '@components/atoms/fontAwesomeIcon';
import { IconSize } from '@components/atoms/fontAwesomeIcon/constants';
import Container from '@components/store/container';

const PRODUCT_PAYMENT_STEP_IMAGE_NAMES: string[] = [
  'b2c_how_to_start_buying_course',
  'b2c_how_to_start_buying_course_credit',
  'b2c_how_to_start_buying_course_qr',
  'b2c_how_to_start_buying_course_paypal',
  'b2c_how_to_start_buying_course_transfer',
];

type ProductPaymentStepItem = {
  key: string;
  title: string;
  content: ReactNode;
};

type ProductPaymentStepProps = {
  scrollElementName: string;
};

const ProductPaymentStep: FC<ProductPaymentStepProps> = ({ scrollElementName }) => {
  const { t } = useTranslation('store_course');
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const theme = useTheme();

  const [expandedKey, setExpandedKey] = useState<string>('');

  const paymentSteps: ProductPaymentStepItem[] = PRODUCT_PAYMENT_STEP_IMAGE_NAMES.map(
    (productPaymentStepImageName, index) => {
      const step = String(index + 1);
      const productPaymentStepImage = new URL(
        `/assets/product-detail/${productPaymentStepImageName}.jpg`,
        config.IMAGE_CDN_HOST,
      ).toString();

      return {
        key: step,
        title: t(`course_detail.course_payment_step.step${step}`),
        content: <Box component="img" src={productPaymentStepImage} width="100%" />,
      };
    },
  );

  const handleChange = (value: string) => (_event: SyntheticEvent, newExpanded: boolean) => {
    setExpandedKey(newExpanded ? value : '');
  };

  return (
    <Element name={scrollElementName}>
      <Box ref={ref} minHeight={{ xs: '486px', md: '469px' }} bgcolor={theme.palette.statePrimaryOverlay.pressed}>
        {inView ? (
          <Container containerSx={{ px: { xs: 3, lg: 0 }, py: 8, textAlign: 'center' }}>
            <Typography pb={0.5} variant="h4">
              {t('course_detail.course_payment_step.title')}
            </Typography>
            <Typography variant="body1lanna">{t('course_detail.course_payment_step.subtitle')}</Typography>

            <Box maxWidth={752} mx="auto" mt={4.5}>
              {paymentSteps.map(({ key, title, content }) => {
                return (
                  <Box key={key}>
                    <Accordion
                      expanded={expandedKey === key}
                      onChange={handleChange(key)}
                      sx={{
                        my: 2,
                        borderRadius: 1,
                        boxShadow: 'none',
                        border: `1px solid ${theme.palette.grey[400]}`,
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <FontAwesomeIcon
                            icon="fa-regular fa-angle-down"
                            color={theme.palette.primary.main}
                            size={IconSize.sm}
                          />
                        }
                      >
                        <Typography variant="h5" fontWeight={500} textAlign="start">
                          {title}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>{content}</AccordionDetails>
                    </Accordion>
                  </Box>
                );
              })}
            </Box>
          </Container>
        ) : null}
      </Box>
    </Element>
  );
};

export default ProductPaymentStep;
